// The one way a page plays sound. Three verbs, each meaning one thing on
// every surface (docs/project/playback-audit.md §5.2):
//
//   Transport — the arrangement from the insert marker. Stop ends the
//               transport AND every preview; launched pads keep going, as
//               Live's Stop leaves the Session View running. Panic (double
//               Stop) silences everything and puts the marker back at 1.1.1.
//   Launch    — Session pads, always on the launch grid: a pad, a scene, a
//               lane's stop, stop-all, back to arrangement.
//   Preview   — "hear this": a clip, a library item, a note, an instrument.
//               Through its lane, in time with whatever runs when asked to
//               sync, looping for loops, one at a time with the last click
//               winning — and ALONE: the transport and the launched pads duck
//               under a clip preview and come back when it stops. Notes
//               beside it, polyphonic, ducking nothing.
//
// A page gets its engine here and nowhere else: `useArrangementPlayer` and
// `useLaunchControls` are this hook's parts (test/playback-boundary.spec.ts
// keeps it so). The raw player api is handed back too — mixer levels,
// meters, the recorder, the clip editor's waveform all read it — but the
// verbs above are what buttons call.

import { useCallback, useEffect, useMemo } from "react";
import type { Arrangement, Lane, LaneClip } from "@web-shared/lib/arrangement";
import type { AuditionOptions, ClipSource, MidiNoteEvent, MidiSource } from "@web-shared/lib/audio-engine";
import { stopExternalPreviews } from "@web-shared/lib/external-previews";
import type { MidiStage } from "@web-shared/lib/midi-effects";
import { clipAuditionSource } from "@web-shared/lib/player-sources";
import { useArrangementPlayer, type ArrangementPlayerApi } from "@web-shared/hooks/useArrangementPlayer";
import { useLaunchControls, type LaunchControls } from "@web-shared/hooks/useLaunchControls";

export interface TransportVerbs {
  /** Space / ▶: play from the insert marker; pressed while playing, Stop. */
  play: () => void;
  /** ■: the transport back to the insert marker, every preview silenced;
   * launched pads play on. */
  stop: () => void;
  /** ■ ■: everything, now — pads too — and the marker back to the start. */
  panic: () => void;
  playing: boolean;
}

export interface PreviewVerbs {
  /** Hear one clip on its own — its bounce or object as audio, its notes as
   * synth — looping, in time with whatever runs. Pressed on the clip
   * already previewing, stops it. Null when the clip has nothing to hear. */
  clip: (clip: LaneClip, lane: Lane, options?: AuditionOptions) => void;
  /** Hear a source that is already built (a library item). */
  source: (src: ClipSource | MidiSource, options?: AuditionOptions) => void;
  /** One note through its lane, now; returns its release (key up). */
  note: (laneId: string, note: MidiNoteEvent) => () => void;
  /** Silence every preview — the engine's and any <audio> one registered
   * with `lib/external-previews`. */
  stop: () => void;
  /** Id of the clip previewing (`audition:<clipId>` for a clip). */
  auditioningId: string | null;
  /** Id of the clip asked for but not sounding yet. */
  pendingId: string | null;
  /** Whether this clip is the one previewing. */
  isPreviewing: (clipId: string) => boolean;
}

export interface Playback {
  transport: TransportVerbs;
  launch: LaunchControls;
  preview: PreviewVerbs;
  /** The engine's full api, for everything that is not a verb. */
  player: ArrangementPlayerApi;
}

export interface PlaybackOptions {
  prefetch?: boolean;
  instruments?: Readonly<Record<string, string>>;
  /** The arrangement launches read their clips from. */
  arrangement: Arrangement | null;
  bpm: number;
  midiEffectsByLane?: Record<string, readonly MidiStage[]>;
  /** How a clip's audio is fetched where the lane's object needs a URL of
   * its own (the browser room serves public blobs same-origin). */
  sourceFor?: (clip: LaneClip, lane: Lane) => ClipSource | MidiSource | null;
}

export function usePlayback(
  sources: ClipSource[],
  fallbackDuration: number,
  midi: MidiSource[],
  options: PlaybackOptions,
): Playback {
  const player = useArrangementPlayer(sources, fallbackDuration, midi, {
    ...(options.prefetch !== undefined ? { prefetch: options.prefetch } : {}),
    ...(options.instruments ? { instruments: options.instruments } : {}),
  });
  const launch = useLaunchControls(player, options.arrangement, options.bpm, options.midiEffectsByLane, options.sourceFor);

  const { stopAudition, playFromInsert, stopToInsert, stop: stopEverything, returnToStart, auditioningId, auditionPendingId, auditionClip, auditionMidiClip, auditionNote, status, refreshAudition } = player;
  const { bpm, midiEffectsByLane, sourceFor, arrangement } = options;

  // An edit reaches the preview: the clip previewing is rebuilt from the
  // arrangement it is in whenever that changes, and restarted as edited if
  // it no longer sounds the same (on the next bar line).
  useEffect(() => {
    if (!arrangement || !auditioningId?.startsWith("audition:")) return;
    const clipId = auditioningId.slice("audition:".length);
    for (const lane of arrangement.lanes) {
      const clip = lane.clips.find((c) => c.id === clipId) ?? lane.sessionClips.find((c) => c.id === clipId);
      if (!clip) continue;
      const src = sourceFor ? sourceFor(clip, lane) : clipAuditionSource(lane, clip, bpm, { effects: midiEffectsByLane?.[lane.id] });
      if (src) refreshAudition(src);
      return;
    }
  }, [arrangement, auditioningId, sourceFor, bpm, midiEffectsByLane, refreshAudition]);

  const stopPreviews = useCallback(() => {
    stopAudition();
    stopExternalPreviews();
  }, [stopAudition]);

  const previewSource = useCallback(
    (src: ClipSource | MidiSource, auditionOptions: AuditionOptions = {}) => {
      stopExternalPreviews();
      if ("notes" in src) auditionMidiClip(src, auditionOptions);
      else auditionClip(src, auditionOptions);
    },
    [auditionClip, auditionMidiClip],
  );

  const previewClip = useCallback(
    (clip: LaneClip, lane: Lane, auditionOptions: AuditionOptions = { sync: true, loop: true }) => {
      const src = sourceFor
        ? sourceFor(clip, lane)
        : clipAuditionSource(lane, clip, bpm, { effects: midiEffectsByLane?.[lane.id] });
      if (!src) return;
      if (auditioningId === src.id) {
        stopPreviews();
        return;
      }
      previewSource(src, auditionOptions);
    },
    [sourceFor, bpm, midiEffectsByLane, auditioningId, stopPreviews, previewSource],
  );

  const transport = useMemo<TransportVerbs>(
    () => ({
      play: () => {
        stopPreviews();
        playFromInsert();
      },
      stop: () => {
        stopPreviews();
        stopToInsert();
      },
      panic: () => {
        stopPreviews();
        stopEverything();
        returnToStart();
      },
      playing: status === "playing",
    }),
    [stopPreviews, playFromInsert, stopToInsert, stopEverything, returnToStart, status],
  );

  const preview = useMemo<PreviewVerbs>(
    () => ({
      clip: previewClip,
      source: previewSource,
      note: auditionNote,
      stop: stopPreviews,
      auditioningId,
      pendingId: auditionPendingId,
      isPreviewing: (clipId) => auditioningId === `audition:${clipId}`,
    }),
    [previewClip, previewSource, auditionNote, stopPreviews, auditioningId, auditionPendingId],
  );

  return useMemo(() => ({ transport, launch, preview, player }), [transport, launch, preview, player]);
}
